import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { handleErrorSilently } from '../util/handleErrorSilently';
import { mapAccepted } from '../util/mapAccepted';
import { ContractDocument } from '../../types/contract/contracts-api.types';
import { RfpWithEmbeddedContracts } from '../../types/measurement/contract';
import { Identifier } from '../../types/identifiers/identifier';
import { noDefaultErrorHandler } from '../../services/interceptors/error-handler/operators/no-default-error-handler.operator';
import { UploadContractApiResponse } from '../../modules/upload/upload-requests-api/upload-requests-api.service';

interface ContractRequestResponse {
  isSuccess: boolean;
  errorMessage: string | null;
  signingUrl: string;
  redirect: boolean;
  redirectTarget: string;
}

@Injectable({
  providedIn: 'root',
})
export class ContractApiService {
  baseUrl = '/aptitude/api';
  contractBaseUrl = '/aptitude/api/contracts/';
  constructor(private http: HttpClient) {}

  getContractDocuments(contractId: number) {
    return this.http.get<ContractDocument[]>(
      `${this.contractBaseUrl}${contractId}/documents`,
    );
  }

  getContractsByRfpId(rfpId: number) {
    let params: HttpParams = new HttpParams();

    params = params.set('rfpId', rfpId.toString(10));

    return this.http.get<RfpWithEmbeddedContracts>(
      `${this.baseUrl}/contracts`,
      {
        params,
      },
    );
  }

  getSystemsForContract(contractId: number) {
    return this.http.get<Array<Identifier>>(
      `${this.contractBaseUrl}${contractId}/partners`,
    );
  }

  getFacilitiesForContract(contractId: number) {
    return this.http.get<SystemToFacilitiesMap>(
      `${this.contractBaseUrl}${contractId}/products/facilities`,
    );
  }

  prepareContractForReview(cpaId: number): Observable<ContractRequestResponse> {
    return this.http
      .post(
        `${this.baseUrl}/contractrequests`,
        {
          contractId: cpaId,
          contractRequestType: 'PREPARE_CONTRACT_FOR_REVIEW',
        },
        {
          observe: 'response',
        },
      )
      .pipe(
        map(mapAccepted()),
        noDefaultErrorHandler(),
        catchError(handleErrorSilently),
      ) as Observable<ContractRequestResponse>;
  }

  prepareContractForSigning(
    cpaId: number,
    email: boolean | undefined,
  ): Observable<ContractRequestResponse> {
    return this.http
      .post(
        `${this.baseUrl}/contractrequests`,
        {
          contractId: cpaId,
          contractRequestType: email
            ? 'PREPARE_CONTRACT_FOR_SIGNING_FROM_EMAIL'
            : 'PREPARE_CONTRACT_FOR_SIGNING',
        },
        {
          observe: 'response',
        },
      )
      .pipe(
        map(mapAccepted()),
        noDefaultErrorHandler(),
        catchError(handleErrorSilently),
      ) as Observable<ContractRequestResponse>;
  }

  shareContract(
    cpaId: number,
    message: string,
    shareType: string,
    userIds: Array<number>,
  ): Observable<ContractRequestResponse> {
    return this.http
      .post(
        `${this.baseUrl}/contractrequests`,
        {
          contractId: cpaId,
          contractRequestType:
            shareType === 'review' ? 'SHARE_FOR_REVIEW' : 'SHARE_FOR_SIGNING',
          message,
          userIds,
        },
        {
          observe: 'response',
        },
      )
      .pipe(
        map(mapAccepted()),
        noDefaultErrorHandler(),
        catchError(handleErrorSilently),
      ) as Observable<ContractRequestResponse>;
  }

  uploadContract(
    files: File[], // Taking array but only supporting one file for now.
    requestType: string,
    cpaId: number,
    previousDocumentUploadDate: number | null,
  ): Observable<UploadContractApiResponse> {
    // This API only supports formData content type so we're creating a form data object.
    const formData = new FormData();
    if (previousDocumentUploadDate) {
      formData.append(
        'previousDocumentUploadDate',
        previousDocumentUploadDate.toString(),
      );
    }
    formData.append('formDataBodyPart', files[0]);
    formData.append('fileName', files[0].name);
    formData.append('filePath', '/');
    formData.append('force', 'false');
    formData.append('requestType', requestType);

    return this.http.post(
      `/aptitude/api/contracts/${cpaId}/upload`,
      formData,
    ) as Observable<UploadContractApiResponse>;
  }
}

export interface SystemToFacilitiesMap {
  [systemId: number]: Array<Identifier>;
}
